<template>
  <v-container>
    <v-row v-if="isLoading">
      <v-col cols="12">
        <v-skeleton-loader type="card" />
      </v-col>
      <v-col cols="12">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>
    <v-fade-transition>
      <v-row v-if="!isLoading">
        <v-col v-if="$can('company:update')" cols="12">
          <v-card flat>
            <v-card-text class="d-flex align-center justify-end ga-3">
              <v-btn variant="text" @click="onClose"> Cancelar </v-btn>
              <v-btn
                color="primary"
                variant="tonal"
                @click="onSubmit"
                :loading="isSubmitting"
              >
                Salvar Alterações
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12">
          <basic-company-info-form
            ref="infoForm"
            :form="form"
            :can-edit-cnpj="canEditCnpj"
            :disabled="!$can('company:update')"
          />
        </v-col>
        <v-col cols="12">
          <company-address-form
            ref="addressForm"
            :form="form"
            :disabled="!$can('company:update')"
          />
        </v-col>
        <v-col v-if="isTenant && $can('company:update')" cols="12">
          <v-card flat>
            <v-card-title>Escopos de Custódia</v-card-title>
            <v-divider />
            <v-card-text>
              <custody-scopes v-model="form.custody_scopes" />
            </v-card-text>
          </v-card>
        </v-col>
        <v-col v-if="isTenant && $can('company:update')" cols="12">
          <v-card flat>
            <v-card-title>Configurações de API</v-card-title>
            <v-divider />
            <v-card-text>
              <v-switch
                v-model="form.api_access_enabled"
                label="Habilitar acesso via API"
                :disabled="isSubmitting"
                color="primary"
                hide-details
              />
              <div class="text-caption text-medium-emphasis mt-2">
                Permite que este parceiro crie credenciais para acessar a API
                pública do Veraciti.
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-fade-transition>
  </v-container>
</template>

<script setup>
import { ref, computed, watch, inject } from "vue";
import { useRoute, useRouter } from "vue-router";
import BasicCompanyInfoForm from "@/views/company/BasicCompanyInfoForm";
import CompanyAddressForm from "@/views/company/CompanyAddressForm";
import CustodyScopes from "@/components/CustodyScopes.vue";
import { emptyToNull, onlyNumbers } from "@/utils";
import {
  useCompanyQuery,
  useUpdateCompanyMutation,
} from "@/queries/modules/companies";
import { useSnackbar } from "@/plugins/snackbar";
import { useBreadcrumbTitle } from "@/composables/useBreadcrumbs";
import { useToolbarLoading } from "@/plugins/toolbar-loading";

const route = useRoute();
const router = useRouter();
const snackbar = useSnackbar();
const $can = inject("$can");
const $bar = useToolbarLoading();

// Refs
const infoForm = ref(null);
const addressForm = ref(null);
const originalCnpj = ref("");
const isTenant = ref(false);
const form = ref({
  hubType: null,
  razao_social: "",
  nome_fantasia: "",
  phone: "",
  cnpj: "",
  cep: "",
  neighborhood: "",
  state_inscription: "",
  municipal_inscription: "",
  street: "",
  city: "",
  complement: "",
  state: "",
  number: "",
  company_type: null,
  codigo_municipio: "",
  csc: "",
  id_csc: "",
  sefazKey: "",
  cnae: "",
  codigo_regime_tributario: "",
  custody_scopes: ["custody"],
  api_access_enabled: false,
});

// Computed
const companyId = computed(() => {
  if (route.meta.type === "tenant") {
    return route.params.tenant_id ? Number(route.params.tenant_id) : null;
  }
  return route.params.company_id ? Number(route.params.company_id) : null;
});

const canEditCnpj = computed(() => {
  return isTenant.value && originalCnpj.value.length !== 14;
});

// TanStack Query - fetch company
const {
  data: companyData,
  isLoading,
  error: companyError,
} = useCompanyQuery(companyId);

const companyName = computed(() => companyData.value?.razao_social);
useBreadcrumbTitle(companyName, isLoading);

// TanStack Query - mutation
const updateCompanyMutation = useUpdateCompanyMutation();
const isSubmitting = computed(() => updateCompanyMutation.isPending.value);

// Watch company data to populate form
watch(
  companyData,
  (data) => {
    if (data) {
      originalCnpj.value = data.cnpj || "";
      isTenant.value = data.is_tenant || false;
      form.value.company_type = data.company_type;
      form.value.razao_social = data.razao_social;
      form.value.nome_fantasia = data.nome_fantasia;
      form.value.phone = data.phone;
      form.value.cnpj = data.cnpj;
      form.value.state_inscription = data.state_inscription;
      form.value.sefazKey = data.sefaz_security_key;
      form.value.cnae = data.cnae;
      form.value.csc = data.csc;
      form.value.id_csc = data.id_csc;
      form.value.municipal_inscription = data.municipal_inscription;
      form.value.cep = data.zipcode;
      form.value.street = data.street;
      form.value.number = data.number;
      form.value.neighborhood = data.neighborhood;
      form.value.city = data.city;
      form.value.state = data.uf;
      form.value.codigo_municipio = data.codigo_municipio;
      form.value.complement = data.complement || "";
      form.value.custody_scopes = data.custody_scopes || ["custody"];
      form.value.api_access_enabled = data.api_access_enabled || false;

      $bar.finish();
    }
  },
  { immediate: true },
);

// Watch for loading state
watch(
  isLoading,
  (loading) => {
    if (loading) {
      $bar.start();
    }
  },
  { immediate: true },
);

// Watch for errors
watch(companyError, (err) => {
  if (err) {
    snackbar.error(err.message);
    $bar.finish();
  }
});

// Methods
const getFormData = () => {
  return {
    id: companyId.value,
    company_type: form.value.company_type,
    razao_social: form.value.razao_social,
    nome_fantasia: form.value.nome_fantasia,
    phone: form.value.phone,
    cnpj: onlyNumbers(form.value.cnpj),
    email: form.value.email,
    state_inscription: onlyNumbers(form.value.state_inscription),
    municipal_inscription: onlyNumbers(form.value.municipal_inscription),
    sefaz_security_key: form.value.sefazKey,
    cnae: onlyNumbers(form.value.cnae),
    csc: emptyToNull(form.value.csc),
    id_csc: emptyToNull(form.value.id_csc),
    zipcode: form.value.cep,
    city: form.value.city,
    complement: form.value.complement,
    neighborhood: form.value.neighborhood,
    number: form.value.number,
    street: form.value.street,
    uf: form.value.state,
    codigo_municipio: form.value.codigo_municipio,
    custody_scopes: form.value.custody_scopes,
    api_access_enabled: form.value.api_access_enabled,
  };
};

const onClose = () => {
  if (route.meta.type === "tenant") {
    router.push({
      name: "tenant.detail",
      params: { tenant_id: route.params.tenant_id },
    });
  } else {
    router.push({
      name: "tenant.companies",
      params: { tenant_id: route.params.tenant_id },
    });
  }
};

const onSubmit = async () => {
  const results = await Promise.all([
    infoForm.value.validate(),
    addressForm.value.validate(),
  ]);
  const allValid = results.every(Boolean);

  if (!allValid) {
    snackbar.warning("Preencha os campos obrigatórios antes de salvar.");
    return;
  }

  try {
    await updateCompanyMutation.mutateAsync(getFormData());
    snackbar.success("As informações foram salvas com sucesso!");
    onClose();
  } catch (error) {
    snackbar.error(
      `Ocorreu um erro ao tentar salvar as informações: ${error.message}`,
    );
  }
};
</script>
